/**
 * New project and Edit project (07 Workshop, rows 5 and 49): a title and the
 * description people read. Write with M7 drafts the description from the
 * title, marked as M7's, with Write again and Undo (the words before the
 * draft come back).
 */
import { useState } from "react";
import { Sparkles, Undo2 } from "lucide-react";
import { KitInput } from "@/app/routes/dashboard/components/kit/Input";
import { KitTextarea } from "@/app/routes/dashboard/components/kit/Textarea";
import { getAiRequestErrorMessage } from "@/lib/api";
import { queue } from "@/components/Toast";
import type { WorkshopProject } from "@/types/workshop.type";
import { useWorkshopProjects } from "../api/useWorkshopProjects";
import { useDraftProjectDescription } from "../api/useWorkshopProjectWriting";
import WorkshopDialog from "./WorkshopDialog";

type Props = {
  brandId: string;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  /** Edit: the project as it is now. New project otherwise. */
  project?: WorkshopProject | null;
  onCreated?: (projectId: string) => void;
};

/** M7's draft, and what the field held before it (Undo puts that back). */
type Draft = { text: string; before: string };

export default function WorkshopProjectDialog({ brandId, isOpen, onOpenChange, project = null, onCreated }: Props) {
  const editing = project !== null;
  const { createProject, updateProject } = useWorkshopProjects(brandId);
  const write = useDraftProjectDescription(brandId);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [draft, setDraft] = useState<Draft | null>(null);

  // Each opening starts from the project as it is (or empty).
  const [seenOpen, setSeenOpen] = useState(false);
  if (isOpen !== seenOpen) {
    setSeenOpen(isOpen);
    if (isOpen) {
      setTitle(project?.name ?? "");
      setDescription(project?.description ?? "");
      setDraft(null);
    }
  }

  const drafted = draft !== null && description === draft.text;

  const writeWithM7 = () => {
    const name = title.trim();
    if (!name || write.isPending) return;
    const before = drafted && draft ? draft.before : description;
    write.mutate(
      { title: name, previous: drafted ? description : undefined },
      {
        onSuccess: (text) => {
          setDraft({ text, before });
          setDescription(text);
        },
        onError: (error) =>
          queue.add({
            title: "Creo couldn't write it",
            description: getAiRequestErrorMessage(error),
            variant: "error",
          }),
      },
    );
  };

  const submit = () => {
    const name = title.trim();
    if (!name) return;
    const text = description.trim();
    if (editing && project) {
      updateProject.mutate(
        { projectId: project.id, name, description: text || null },
        {
          onError: (error) =>
            queue.add({
              title: "Couldn't save the project",
              description: getAiRequestErrorMessage(error),
              variant: "error",
            }),
        },
      );
      onOpenChange(false);
      return;
    }
    createProject.mutate(
      { name, ...(text ? { description: text } : {}) },
      {
        onSuccess: (created) => {
          onOpenChange(false);
          onCreated?.(created.id);
        },
        onError: (error) =>
          queue.add({
            title: "Couldn't create the project",
            description: getAiRequestErrorMessage(error),
            variant: "error",
          }),
      },
    );
  };

  return (
    <WorkshopDialog
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      title={editing ? "Edit project" : "New project"}
      description={
        editing
          ? "The description is for people; instructions for Creo are in the project’s panel."
          : "Give it a title and say what it is for. The description is for people; instructions for Creo go in the project’s panel."
      }
      cancelLabel="Cancel"
      confirmLabel={editing ? "Save" : "Create project"}
      onSubmit={submit}
      confirmDisabled={!title.trim()}
      confirmLoading={createProject.isPending}
      form
    >
      <KitInput
        label="Title"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        maxLength={120}
        autoFocus
        placeholder="What is this project called?"
      />
      <div className="ws-dialog-field">
        <KitTextarea
          label="Description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          maxLength={2000}
          rows={3}
          placeholder="What is it for, in a sentence or two?"
        />
        <div className="ws-drafted">
          {drafted ? (
            <span className="ws-drafted-origin">
              <Sparkles aria-hidden="true" />
              Drafted by Creo from the title
            </span>
          ) : null}
          <span className="ws-side-push" />
          <button
            type="button"
            className="ws-text-action"
            onClick={writeWithM7}
            disabled={!title.trim() || write.isPending}
            aria-busy={write.isPending || undefined}
          >
            <Sparkles aria-hidden="true" />
            {write.isPending ? "Writing…" : drafted ? "Write again" : "Write with Creo"}
          </button>
          {drafted && draft ? (
            <button
              type="button"
              className="ws-text-action"
              onClick={() => {
                setDescription(draft.before);
                setDraft(null);
              }}
            >
              <Undo2 aria-hidden="true" />
              Undo
            </button>
          ) : null}
        </div>
      </div>
    </WorkshopDialog>
  );
}
